// Upload progress status component
import React from 'react';
import { CheckCircle2, AlertCircle, RefreshCw, Upload } from 'lucide-react';
import type { UploadProgress } from '../../../shared/types/analytics';

interface UploadStatusProps {
  progress: UploadProgress;
  className?: string;
}

export const UploadStatus: React.FC<UploadStatusProps> = ({ 
  progress, 
  className = '' 
}) => {
  const getStageIcon = () => {
    switch (progress.stage) {
      case 'uploading':
        return <Upload className="w-4 h-4 text-blue-500" />;
      case 'parsing':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'deduplicating':
        return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'saving':
        return <RefreshCw className="w-4 h-4 text-green-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <RefreshCw className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStageColor = () => {
    switch (progress.stage) {
      case 'uploading':
      case 'parsing':
        return 'bg-blue-500';
      case 'deduplicating':
        return 'bg-yellow-500';
      case 'saving':
      case 'completed':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStageLabel = () => {
    switch (progress.stage) {
      case 'uploading':
        return 'Uploading';
      case 'parsing':
        return 'Parsing Data';
      case 'deduplicating':
        return 'Checking Duplicates';
      case 'saving':
        return 'Saving';
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Error';
      default:
        return 'Processing';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Progress Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getStageIcon()}
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {getStageLabel()}
          </span>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {progress.progress}%
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${getStageColor()}`}
          style={{ width: `${Math.max(0, Math.min(100, progress.progress))}%` }}
        />
      </div>

      {/* Status Message */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {progress.message}
      </p>

      {/* Processing Details */}
      {(progress.processed_records !== undefined || 
        progress.total_records !== undefined ||
        progress.new_records !== undefined ||
        progress.duplicate_records !== undefined) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
          {progress.total_records !== undefined && (
            <div className="text-center">
              <div className="font-medium text-gray-900 dark:text-white">
                {progress.total_records.toLocaleString()}
              </div>
              <div className="text-gray-500 dark:text-gray-400">Total</div>
            </div>
          )}
          {progress.processed_records !== undefined && (
            <div className="text-center">
              <div className="font-medium text-gray-900 dark:text-white">
                {progress.processed_records.toLocaleString()}
              </div>
              <div className="text-gray-500 dark:text-gray-400">Processed</div>
            </div>
          )}
          {progress.new_records !== undefined && (
            <div className="text-center">
              <div className="font-medium text-green-600 dark:text-green-400">
                {progress.new_records.toLocaleString()}
              </div>
              <div className="text-gray-500 dark:text-gray-400">New</div>
            </div>
          )}
          {progress.duplicate_records !== undefined && (
            <div className="text-center">
              <div className="font-medium text-yellow-600 dark:text-yellow-400">
                {progress.duplicate_records.toLocaleString()}
              </div>
              <div className="text-gray-500 dark:text-gray-400">Duplicates</div>
            </div>
          )}
        </div>
      )}

      {/* Error Details */}
      {progress.stage === 'error' && progress.error_details && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700 dark:text-red-300">
              <p className="font-medium">Error Details:</p>
              <p className="mt-1">{progress.error_details}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Summary */}
      {progress.stage === 'completed' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-700 dark:text-green-300">
              <p className="font-medium">Upload successful!</p>
              {(progress.new_records !== undefined || progress.duplicate_records !== undefined) && (
                <p className="mt-1">
                  {progress.new_records || 0} new records added
                  {progress.duplicate_records ? `, ${progress.duplicate_records} duplicates skipped` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
